/**
 * SPEC-AgentDetail-Revisions — Completion Details card (replaces the v4
 * Completion notes card on the agent surface).
 *
 * Layout (Frames 1–3 of the mockup):
 *   - Header: "Completion Details" left + completed-at timestamp right.
 *     The OnFleet task short ID is intentionally NOT in the header
 *     (Sarah's 2026-05-29 review: too noisy, agents don't read it).
 *   - Scan ID chip — green, renders only when a captured barcode is present.
 *   - Completion note — italic, muted-empty when absent.
 *   - Slack Thread + View in OnFleet buttons at the bottom. Each button
 *     renders independently; the row hides entirely when both URLs are null.
 *
 * Data sources (all derived client-side; no agent-payload-filter changes):
 *   - scanId      → primaryScanId(taskData) — see scan-id-helpers.
 *   - onfleetUrl  → built from `onfleetTaskId` with the senior template.
 *   - slackThreadUrl → from useSlackThreadUrl(deliveryId) at the call site.
 */

import { Starling } from "@/lib/starling-brand";

export interface CompletionDetailsCardProps {
  completedAt?: string | null;
  scanId?: string | null;
  completionNote?: string | null;
  onfleetTaskId?: string | null;
  slackThreadUrl?: string | null;
}

const TOKENS = {
  card: "#FFFFFF",
  border: "#E5E7EB",
  sub: "#F3F4F6",
  text: "#111827",
  txt2: "#6B7280",
  txt3: "#9CA3AF",
  scan: "#16A34A",
  scanBg: "#DCFCE7",
  slack: "#4A154B",
  onfleet: "#2563EB",
} as const;

function buildOnfleetUrl(onfleetTaskId: string): string {
  // Same template as qa-detail-v3.tsx:4239 — kept byte-identical so a future
  // OnFleet dashboard URL change updates both surfaces with one edit.
  return `https://app.onfleet.com/dashboard#/manage?taskEdit=false&open=task&taskId=${onfleetTaskId}`;
}

export function CompletionDetailsCard({
  completedAt,
  scanId,
  completionNote,
  onfleetTaskId,
  slackThreadUrl,
}: CompletionDetailsCardProps) {
  const hasButtons = !!(onfleetTaskId || slackThreadUrl);
  return (
    <div
      data-testid="card-completion-details"
      style={{
        background: TOKENS.card,
        border: `1px solid ${TOKENS.border}`,
        borderRadius: 8,
        padding: "12px 16px",
        boxShadow: "0 1px 2px rgba(16,24,40,.06)",
        fontFamily: Starling.fontBody,
        color: TOKENS.text,
      }}
    >
      {/* Header — title left, timestamp right; NO OnFleet task ID. */}
      <div
        style={{
          fontFamily: Starling.fontHead,
          fontSize: 14,
          fontWeight: 600,
          marginBottom: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>Completion Details</span>
        {completedAt && (
          <span
            data-testid="completion-timestamp"
            style={{ fontSize: 11, color: TOKENS.txt3, fontWeight: 500 }}
          >
            {formatDate(completedAt)}
          </span>
        )}
      </div>

      {/* Scan ID chip — conditional. */}
      {scanId && (
        <div
          data-testid="completion-scan-id"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: TOKENS.scanBg,
            color: TOKENS.scan,
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 6,
            padding: "5px 10px",
            marginBottom: 8,
          }}
        >
          <span aria-hidden style={{ fontWeight: 800 }}>⊞</span>
          <span>Scan ID:</span>
          <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontWeight: 700 }}>{scanId}</span>
        </div>
      )}

      {/* Completion note — italic when present, muted-empty when absent. */}
      {completionNote ? (
        <div
          data-testid="completion-note"
          style={{ fontSize: 13, color: TOKENS.text, lineHeight: 1.5, padding: "4px 2px" }}
        >
          {completionNote}
        </div>
      ) : (
        <div
          data-testid="completion-note"
          style={{ fontSize: 12, color: TOKENS.txt3, fontStyle: "italic", padding: "4px 2px" }}
        >
          No completion notes submitted.
        </div>
      )}

      {/* Action buttons row — at the BOTTOM of the card. Hides entirely
          when both Slack + OnFleet URLs are absent. */}
      {hasButtons && (
        <div
          style={{
            display: "flex",
            gap: 7,
            marginTop: 10,
            flexWrap: "wrap",
          }}
        >
          {slackThreadUrl && (
            <a
              href={slackThreadUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="completion-slack"
              style={{
                background: TOKENS.slack,
                color: "#fff",
                border: 0,
                padding: "6px 11px",
                borderRadius: 6,
                fontFamily: Starling.fontBody,
                fontSize: 11.5,
                fontWeight: 700,
                cursor: "pointer",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                letterSpacing: "0.02em",
              }}
            >
              💬 SLACK THREAD
            </a>
          )}
          {onfleetTaskId && (
            <a
              href={buildOnfleetUrl(onfleetTaskId)}
              target="_blank"
              rel="noreferrer"
              data-testid="completion-onfleet"
              style={{
                background: TOKENS.onfleet,
                color: "#fff",
                border: 0,
                padding: "6px 11px",
                borderRadius: 6,
                fontFamily: Starling.fontBody,
                fontSize: 11.5,
                fontWeight: 700,
                cursor: "pointer",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                letterSpacing: "0.02em",
              }}
            >
              ↗ VIEW IN ONFLEET
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default CompletionDetailsCard;
